import React, { useState, useEffect, useRef } from 'react';
import { FileDown } from 'lucide-react';
import { fetchFinancialYears, fetchFYMRevenueReport } from '../services/revenueService';
// Imported the requested subcounty service
import { fetchSubcounties } from '../services/subcountyService';
import { exportDashboardToPDF } from '../utils/exportUtils';

const RevenueFYMReportTable = () => {
    const [financialYears, setFinancialYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState('All');
    const [reportData, setReportData] = useState([]);
    const [subCounties, setSubCounties] = useState([]); // This stores the list of unique IDs found in the report data
    const [subCountyMaster, setSubCountyMaster] = useState([]); // Master metadata list from subcountyService
    const [loading, setLoading] = useState(false);
    const dashboardRef = useRef(null);

  // Expand states tracking
  const [expandedYears, setExpandedYears] = useState({});
  const [expandedMonths, setExpandedMonths] = useState({});

// 1. Fetch distinct financial years AND subcounties master data on mount
  // 1. Fetch distinct financial years AND subcounties master data on mount
useEffect(() => {
  const loadInitialData = async () => {
    try {
      const [years, masterSubcounties] = await Promise.all([
        fetchFinancialYears(),
        fetchSubcounties()
      ]);
      
      console.log("Clean Financial Years Array in React:", years);

      // Save directly into state since backend service is now fixed
      if (Array.isArray(years)) {
        setFinancialYears(years);
      } else {
        setFinancialYears([]);
      }

      setSubCountyMaster(masterSubcounties);
    } catch (err) {
      console.error('Failed to load initial configuration data', err);
    }
  };
  loadInitialData();
}, []);

  useEffect(() => {
    const loadReport = async () => {
      setLoading(true);
      try {
        const result = await fetchFYMRevenueReport(selectedYear);
        setReportData(result.data);
        setSubCounties(result.subCounties);
      } catch (err) {
        console.error('Failed to load report data', err);
      } finally {
        setLoading(false);
      }
    };
    loadReport();
  }, [selectedYear]);

  // Helper utility to lookup Subcounty Name based on ID
  /*const getSubCountyName = (id) => {
    const match = subCountyMaster.find(sc => String(sc.id) === String(id));
    // Fallback to "Sub County ID" if lookup master metadata hasn't loaded or isn't matching
    return match ? match.name : `Sub County ${id}`;
  };*/
  const getSubCountyName = (id) => {
  const match = subCountyMaster.find(sc => String(sc.id) === String(id));
  if (match && match.name) {
    return match.name
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  
  return `Sub County ${id}`;
};

  // Helper to build a sequential tree from rows pre-sorted by DB
  const buildOrderedTree = (rawData) => {
    const fyList = [];
    const fyMap = {};

    rawData.forEach(row => {
      const { financial_year, calendar_year, calendar_month, rev_stream, sub_county_id, total_amount } = row;
      const amount = parseFloat(total_amount) || 0;
      const monthYearKey = `${calendar_month} ${parseInt(calendar_year)}`;

      // 1. Process Financial Year Node
      if (!fyMap[financial_year]) {
        fyMap[financial_year] = {
          financial_year,
          total: 0,
          subCounties: {},
          monthsMap: {},
          monthsList: []
        };
        fyList.push(fyMap[financial_year]);
      }
      const fyNode = fyMap[financial_year];
      fyNode.total += amount;
      fyNode.subCounties[sub_county_id] = (fyNode.subCounties[sub_county_id] || 0) + amount;

      // 2. Process Month Node
      if (!fyNode.monthsMap[monthYearKey]) {
        fyNode.monthsMap[monthYearKey] = {
          monthYearKey,
          total: 0,
          subCounties: {},
          streamsMap: {},
          streamsList: []
        };
        fyNode.monthsList.push(fyNode.monthsMap[monthYearKey]);
      }
      const monthNode = fyNode.monthsMap[monthYearKey];
      monthNode.total += amount;
      monthNode.subCounties[sub_county_id] = (monthNode.subCounties[sub_county_id] || 0) + amount;

      // 3. Process Revenue Stream Node
      if (!monthNode.streamsMap[rev_stream]) {
        monthNode.streamsMap[rev_stream] = {
          rev_stream,
          total: 0,
          subCounties: {}
        };
        monthNode.streamsList.push(monthNode.streamsMap[rev_stream]);
      }
      const streamNode = monthNode.streamsMap[rev_stream];
      streamNode.total += amount;
      streamNode.subCounties[sub_county_id] = (streamNode.subCounties[sub_county_id] || 0) + amount;
    });

    return fyList;
  };

  const toggleYear = (fy) => {
    setExpandedYears(prev => ({ ...prev, [fy]: !prev[fy] }));
  };

  const toggleMonth = (monthKey) => {
    setExpandedMonths(prev => ({ ...prev, [monthKey]: !prev[monthKey] }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  };

  const structuredTree = buildOrderedTree(reportData);

  return (
    <div ref={dashboardRef} style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      {/* Centered Top Header Wrapper */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        textAlign: 'center',
        marginBottom: '30px',
        width: '100%' /* Guarantees the wrapper handles full-width row alignments */
      }}>
        <h2 style={{ fontWeight: 'bold', marginBottom: '15px', color: '#1e293b' }}>
          Monthly Total Revenue Generation Report
        </h2>
        
        {/* Centered Filter Section */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '10px',
          backgroundColor: '#f1f5f9',
          padding: '10px 20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '20px' 
        }}>
          <label htmlFor="fy-filter" style={{ fontWeight: 'bold', color: '#475569' }}>
            Select a Financial Year [Default: ALL]:
          </label>
          <select 
            id="fy-filter"
            value={selectedYear} 
            onChange={(e) => setSelectedYear(e.target.value)}
            style={{ 
              padding: '8px 16px', 
              borderRadius: '6px', 
              border: '1px solid #cbd5e1',
              backgroundColor: '#ffffff',
              fontSize: '14px',
              cursor: 'pointer',
              outline: 'none'
            }}
          >
            <option value="All">All Financial Years</option>
            {Array.isArray(financialYears) && financialYears.map((item, index) => {
              const fyValue = item && typeof item === 'object' ? item.financial_year : item;
              if (!fyValue) return null;
              return (
                <option key={`fy-opt-${fyValue}-${index}`} value={fyValue}>
                  {fyValue}
                </option>
              );
            })}
          </select>
        </div>

        {/* Full-width Horizontal Flex Row Container */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          marginTop: '5px',
          padding: '0 5px'
        }}>
          {/* Left Spacer: Keeps the center Notes box mathematically centered on the screen */}
          <div style={{ width: '110px' }} />

          {/* Clean, Grouped Notes Container */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            backgroundColor: '#f8fafc',
            border: '1px dashed #cbd5e1',
            padding: '8px 16px',
            borderRadius: '6px',
            maxWidth: '500px',
            textAlign: 'left' /* Aligns lines cleanly inside the box container */
          }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#64748b', fontWeight: '700' }}>
              💡 <strong style={{ color: '#475569' }}>Note:</strong> 
            </p>
            <p style={{ margin: 0, fontSize: '12px', color: '#64748b', fontWeight: '700' }}>
              Click on <span style={{ color: '#1e293b', fontWeight: 'bold' }}>'►'</span> against the Financial Year to expand Monthly Revenue rows.
            </p>
            <p style={{ margin: 0, fontSize: '12px', color: '#64748b', fontWeight: '700' }}>
              Click on <span style={{ color: '#1e293b', fontWeight: 'bold' }}>'►'</span> against any Month to view breakdown by Revenue Streams.
            </p>
          </div>

          {/* Far Right Corner Button Placement */}
          <div style={{ width: '110px', display: 'flex', justifyContent: 'flex-end' }}>
            <button 
              onClick={() => exportDashboardToPDF(dashboardRef, `Monthly_Total_Revenue_Generation_Report`)}
              className="flex items-center gap-2 px-3 py-1.5 bg-slate-900 text-white text-[10px] font-black rounded-lg hover:bg-slate-800 active:scale-95 shadow-md whitespace-nowrap"
            >
              <FileDown size={14} /> EXPORT PDF
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div>Loading report records...</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>
                <th style={{ padding: '12px', border: '1px solid #cbd5e1' }}>Financial Year</th>
                <th style={{ padding: '12px', border: '1px solid #cbd5e1' }}>Month Year / Revenue Stream</th>
                <th style={{ padding: '12px', border: '1px solid #cbd5e1' }}>Totals</th>
                
                {/* Explicitly guarantee unique tracking keys for subcounties header cells */}
                {Array.isArray(subCounties) && subCounties.map((id, index) => (
                  <th key={`sc-header-${id}-${index}`} style={{ padding: '12px', border: '1px solid #cbd5e1' }}>
                    {getSubCountyName(id)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {structuredTree.map((fy, fyIdx) => {
                const isFyExpanded = !!expandedYears[fy.financial_year];
                
                // Explicitly unique key for the Financial Year container row
                const fyRowKey = `fy-row-${fy.financial_year}-${fyIdx}`;

                return (
                  <React.Fragment key={`fy-block-${fy.financial_year}-${fyIdx}`}>
                    
                    {/* LEVEL 1: Financial Year Row */}
                    <tr key={fyRowKey} style={{ backgroundColor: '#e2e8f0', fontWeight: 'bold' }}>
                      <td style={{ padding: '10px', border: '1px solid #cbd5e1' }}>
                        <button 
                          onClick={() => toggleYear(fy.financial_year)} 
                          style={{ marginRight: '8px', cursor: 'pointer', background: 'none', border: 'none', fontSize: '14px' }}
                        >
                          {isFyExpanded ? '▼' : '►'}
                        </button>
                        {fy.financial_year}
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #cbd5e1', color: '#64748b' }}>All Months</td>
                      <td style={{ padding: '10px', border: '1px solid #cbd5e1' }}>{formatCurrency(fy.total)}</td>
                      {subCounties.map((id, scIdx) => (
                        <td key={`fy-sc-cell-${fy.financial_year}-${id}-${scIdx}`} style={{ padding: '10px', border: '1px solid #cbd5e1' }}>
                          {formatCurrency(fy.subCounties[id] || 0)}
                        </td>
                      ))}
                    </tr>

                    {/* LEVEL 2: Monthly Breakdown rows */}
                    {isFyExpanded && fy.monthsList.map((month, monthIdx) => {
                      const monthKey = `${fy.financial_year}-${month.monthYearKey}`;
                      const isMonthExpanded = !!expandedMonths[monthKey];
                      
                      // Explicitly unique key for the Month row
                      const monthRowKey = `month-row-${monthKey}-${monthIdx}`;

                      return (
                        <React.Fragment key={`month-block-${monthKey}-${monthIdx}`}>
                          <tr key={monthRowKey} style={{ backgroundColor: '#f8fafc' }}>
                            <td style={{ padding: '10px', border: '1px solid #cbd5e1', paddingLeft: '25px', color: '#94a3b8' }}>—</td>
                            <td style={{ padding: '10px', border: '1px solid #cbd5e1', fontWeight: '600' }}>
                              <button 
                                onClick={() => toggleMonth(monthKey)} 
                                style={{ marginRight: '8px', cursor: 'pointer', background: 'none', border: 'none', fontSize: '12px' }}
                              >
                                {isMonthExpanded ? '▼' : '►'}
                              </button>
                              {month.monthYearKey}
                            </td>
                            <td style={{ padding: '10px', border: '1px solid #cbd5e1', fontWeight: '600' }}>{formatCurrency(month.total)}</td>
                            {subCounties.map((id, scIdx) => (
                              <td key={`month-sc-cell-${monthKey}-${id}-${scIdx}`} style={{ padding: '10px', border: '1px solid #cbd5e1', fontWeight: '600' }}>
                                {formatCurrency(month.subCounties[id] || 0)}
                              </td>
                            ))}
                          </tr>

                          {/* LEVEL 3: Revenue Streams Breakdown rows */}
                          {isMonthExpanded && month.streamsList.map((stream, streamIdx) => {
                            
                            // Explicitly unique key for the Revenue Stream sub-row
                            const streamRowKey = `stream-row-${monthKey}-${stream.rev_stream}-${streamIdx}`;

                            return (
                              <tr key={streamRowKey} style={{ fontStyle: 'italic', color: '#334155' }}>
                                <td style={{ padding: '8px', border: '1px solid #e2e8f0' }}></td>
                                <td style={{ padding: '8px', border: '1px solid #e2e8f0', paddingLeft: '40px' }}>
                                  ↳ {stream.rev_stream}
                                </td>
                                <td style={{ padding: '8px', border: '1px solid #e2e8f0' }}>{formatCurrency(stream.total)}</td>
                                {subCounties.map((id, scIdx) => (
                                  <td key={`stream-sc-cell-${monthKey}-${stream.rev_stream}-${id}-${scIdx}`} style={{ padding: '8px', border: '1px solid #e2e8f0' }}>
                                    {formatCurrency(stream.subCounties[id] || 0)}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              {structuredTree.length === 0 && (
                <tr>
                  <td colSpan={3 + subCounties.length} style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                    No revenue records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RevenueFYMReportTable;